import axiosClient from "./axios";
import axiosImage from "./axiosImage";

/**
 * Yeni Yıl Dileği Servisi
 * 
 * Bu servis Yeni Yıl Dileği modülü için gerekli API çağrılarını içerir.
 * Backend API: /module/new-year-wishes/*
 */

/**
 * Yeni yıl dileği oluştur
 * @param {Object} data - Dilek verisi
 * @param {string} data.title - Dilek başlığı
 * @param {string} data.content - Dilek içeriği
 * @param {string} data.wish_date - Dilek açılma tarihi (YYYY-MM-DD HH:MM:SS)
 * @param {string} data.timezone - Zaman dilimi (Europe/Istanbul)
 * @param {string} data.receiver_id - Alıcının UUID'si
 * @param {string} data.receiver_mail - Alıcının email'i
 * @param {string} [data.id] - Gönderen Jodiac kullanıcısı UUID (opsiyonel)
 * @param {string} [data.fullname] - Gönderen adı soyadı (opsiyonel)
 * @returns {Promise} - API yanıtı
 */
export const postNewYearWish = async (data) => {
  try {
    const response = await axiosClient.post(`/module/new-year-wishes/create`, data);
    return response.data;
  } catch (error) {
    console.error("Yeni yıl dileği gönderilirken hata:", error);
    throw error;
  }
};

/**
 * Yeni yıl dileği listesini getir
 * @returns {Promise} - API yanıtı (content ve photo_url döndürülmez)
 */
export const getNewYearWishList = async () => {
  try {
    const response = await axiosClient.get(`/module/new-year-wishes/list`);
    return response.data;
  } catch (error) {
    console.error("Yeni yıl dileği listesi alınırken hata:", error);
    throw error;
  }
};

/**
 * BUSINESS rolü için yeni yıl dileği listesini getir
 * Locked olsa bile tüm içerikleriyle birlikte döner
 * @param {number} [limit] - Getirilecek maksimum dilek sayısı (opsiyonel)
 * @returns {Promise} - API yanıtı (tüm veriler dahil)
 */
export const getNewYearWishListBusiness = async (limit) => {
  try {
    const params = limit ? { limit } : {};
    const response = await axiosClient.get(`/module/new-year-wishes/business/list`, { params });
    return response.data;
  } catch (error) {
    console.error("Yeni yıl dileği listesi (business) alınırken hata:", error);
    throw error;
  }
};

/**
 * Tek bir yeni yıl dileğinin detayını getir
 * @param {string} uuid - Dileğin UUID'si
 * @returns {Promise} - API yanıtı (content ve photo_url dahil)
 */
export const getNewYearWish = async (uuid) => {
  try {
    const response = await axiosClient.get(`/module/new-year-wishes/${uuid}`);
    return response.data;
  } catch (error) {
    console.error("Yeni yıl dileği alınırken hata:", error);
    throw error;
  }
};

/**
 * Yeni yıl dileği durumunu güncelle
 * @param {string} uuid - Dileğin UUID'si
 * @param {number} action - İşlem tipi
 *   1: Kabul et (request_status → accepted)
 *   2: Aç (status → unlocked)
 *   3: Sil
 *   4: Reddet (request_status → rejected)
 * @returns {Promise} - API yanıtı
 */
export const updateNewYearWishStatus = async (uuid, action) => {
  try {
    const response = await axiosClient.post(`/module/new-year-wishes/${uuid}/update`, { action });
    return response.data;
  } catch (error) {
    console.error("Yeni yıl dileği durumu güncellenirken hata:", error);
    throw error;
  }
};

/**
 * Yeni yıl dileğine fotoğraf yükle
 * @param {File} photo - Yüklenecek fotoğraf dosyası
 * @param {string} uuid - Dileğin UUID'si
 * @returns {Promise} - API yanıtı
 */
export const postNewYearWishPhoto = async (photo, uuid) => {
  try {
    const formData = new FormData();
    formData.append('photo', photo);
    
    const response = await axiosImage.post(`/api/module/new-year-wishes/${uuid}/upload-photo`, formData);
    return response.data;
  } catch (error) {
    console.error("Yeni yıl dileği fotoğrafı yüklenirken hata:", error);
    throw error;
  }
};

// Geriye uyumluluk ve kolaylık için wrapper fonksiyonlar

/**
 * Yeni yıl dileğini kabul et
 * @param {string} uuid - Dileğin UUID'si
 * @returns {Promise} - API yanıtı
 */
export const acceptNewYearWish = async (uuid) => {
  return updateNewYearWishStatus(uuid, 1);
};

/**
 * Yeni yıl dileğini aç (unlock)
 * @param {string} uuid - Dileğin UUID'si
 * @returns {Promise} - API yanıtı
 */
export const unlockNewYearWish = async (uuid) => {
  return updateNewYearWishStatus(uuid, 2);
};

/**
 * Yeni yıl dileğini sil
 * @param {string} uuid - Dileğin UUID'si
 * @returns {Promise} - API yanıtı
 */
export const deleteNewYearWish = async (uuid) => {
  return updateNewYearWishStatus(uuid, 3);
};

/**
 * Yeni yıl dileğini reddet
 * @param {string} uuid - Dileğin UUID'si
 * @returns {Promise} - API yanıtı
 */
export const rejectNewYearWish = async (uuid) => {
  return updateNewYearWishStatus(uuid, 4);
};

/**
 * Yeni yıl dileğini token ile erken aç
 * @param {string} uuid - Dileğin UUID'si
 * @returns {Promise} - API yanıtı
 */
export const unlockNewYearWishWithTokens = async (uuid) => {
  try {
    const response = await axiosClient.post(`/module/new-year-wishes/${uuid}/unlock-with-tokens`);
    return response.data;
  } catch (error) {
    console.error("Yeni yıl dileği token ile açılırken hata:", error);
    throw error;
  }
};

/**
 * BUSINESS rolü için yeni yıl dileğini sil
 * @param {string} id - Dileğin UUID'si
 * @returns {Promise} - API yanıtı
 */
export const deleteNewYearWishBusiness = async (id) => {
  try {
    const response = await axiosClient.delete(`/module/new-year-wishes/business/delete`, {
      data: { id }
    });
    return response.data;
  } catch (error) {
    console.error("Yeni yıl dileği (business) silinirken hata:", error);
    throw error;
  }
};

